import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from './SEOHead';
import NewTopBar from './NewTopBar';
import NewHeader from './NewHeader';
import NewFooter from './NewFooter';
import LegalText from './LegalText';

const TermsOfService = () => {
  const { language } = useLanguage();
  const sl = language === 'sl';

  const content = sl ? {
    title: 'Splošni pogoji',
    metaDesc: 'Splošni pogoji poslovanja GENERAL LEDGER d.o.o. — računovodski servis, Rogatec, Slovenija.',
    lastUpdated: 'Zadnja posodobitev: januar 2026',
    intro: 'Ti splošni pogoji urejajo uporabo spletne strani in storitev podjetja GENERAL LEDGER d.o.o. Z uporabo naše spletne strani ali storitev se strinjate s temi pogoji.',
    sections: [
      {
        title: '1. O podjetju',
        content: `GENERAL LEDGER d.o.o.
Celjska cesta 47
3252 Rogatec, Slovenija

Davčna številka: SI12345678
Matična številka: 1234567000
Vpisano v sodni register pri Okrožnem sodišču v Celju`
      },
      {
        title: '2. Obseg storitev',
        content: `Naše storitve vključujejo:

• **Računovodstvo:** vodenje poslovnih knjig, priprava računovodskih izkazov
• **DDV obračuni:** priprava in oddaja DDV napovedi
• **Obračun plač:** obračun plač, prispevkov in davkov
• **Ustanovitev podjetij:** registracija d.o.o., s.p., podružnic
• **Delovna dovoljenja:** pridobivanje delovnih in bivalnih dovoljenj
• **Davčno svetovanje:** optimizacija davčnih obveznosti

Podrobnosti posameznih storitev so določene v posameznih pogodbah.`
      },
      {
        title: '3. Naročilo storitev',
        content: `Storitve lahko naročite:

• Preko spletnega obrazca na naši strani
• Po elektronski pošti na info@general-ledger.eu
• Po telefonu na +386 3 818 40 60
• Osebno v naši pisarni

Po prejemu povpraševanja vam v 48 urah pošljemo pisno ponudbo s fiksno ceno.`
      },
      {
        title: '4. Cene in plačilo',
        content: `• Vse cene so navedene brez DDV, razen če ni izrecno navedeno drugače
• Cene so fiksne za celotno obdobje pogodbe
• Račune izdajamo mesečno, plačilni rok je 15 dni
• V primeru zamude zaračunamo zakonske zamudne obresti
• Za dodatne storitve izven osnovnega paketa se dogovorimo vnaprej`
      },
      {
        title: '5. Odgovornost',
        content: `• Za pravilnost in pravočasnost opravljenih storitev odgovarjamo v skladu z veljavno zakonodajo
• Ne odgovarjamo za napake, ki nastanejo zaradi nepopolnih ali napačnih podatkov, ki nam jih posredujete
• Naša odgovornost je omejena na višino prejetih plačil v zadnjih 12 mesecih
• Imamo sklenjeno zavarovanje poklicne odgovornosti`
      },
      {
        title: '6. Zaupnost',
        content: `• Vse informacije, ki nam jih zaupate, obravnavamo kot zaupne
• Podatke razkrijemo tretjim osebam samo z vašim soglasjem ali če to zahteva zakon
• Naši zaposleni so zavezani k varovanju poslovnih skrivnosti`
      },
      {
        title: '7. Trajanje in prekinitev',
        content: `• Pogodba se sklene za nedoločen čas, razen če ni dogovorjeno drugače
• Odpoved je možna z enomesečnim odpovednim rokom
• Ob prekinitvi vam vso dokumentacijo predamo urejeno in brez doplačila
• V primeru hujših kršitev lahko pogodbo prekinemo brez odpovednega roka`
      },
      {
        title: '8. Reševanje sporov',
        content: `• Za razlago teh pogojev se uporablja slovensko pravo
• Morebitne spore bomo poskušali rešiti sporazumno
• Če to ne bo mogoče, je pristojno sodišče v Celju`
      },
      {
        title: '9. Spremembe pogojev',
        content: `• Te pogoje lahko kadarkoli spremenimo
• O spremembah vas obvestimo po elektronski pošti
• Nadaljevanje uporabe storitev po spremembi pomeni strinjanje z novimi pogoji`
      }
    ]
  } : {
    title: 'Terms of Service',
    metaDesc: 'Terms of Service for GENERAL LEDGER d.o.o. — accounting firm, Rogatec, Slovenia.',
    lastUpdated: 'Last updated: January 2026',
    intro: 'These terms of service govern the use of the website and services of GENERAL LEDGER d.o.o. By using our website or services, you agree to these terms.',
    sections: [
      {
        title: '1. About the Company',
        content: `GENERAL LEDGER d.o.o.
Celjska cesta 47
3252 Rogatec, Slovenia

VAT number: SI12345678
Registration number: 1234567000
Registered at the District Court in Celje`
      },
      {
        title: '2. Scope of Services',
        content: `Our services include:

• **Accounting:** bookkeeping, preparation of financial statements
• **VAT returns:** preparation and submission of VAT declarations
• **Payroll:** calculation of salaries, contributions and taxes
• **Company formation:** registration of d.o.o., s.p., branches
• **Work permits:** obtaining work and residence permits
• **Tax advisory:** optimisation of tax obligations

Details of individual services are specified in individual contracts.`
      },
      {
        title: '3. Ordering Services',
        content: `You can order services:

• Via the online form on our website
• By email at info@general-ledger.eu
• By phone at +386 3 818 40 60
• In person at our office

Upon receipt of your enquiry, we will send you a written quote with a fixed price within 48 hours.`
      },
      {
        title: '4. Prices and Payment',
        content: `• All prices are quoted exclusive of VAT unless expressly stated otherwise
• Prices are fixed for the entire contract period
• Invoices are issued monthly, payment term is 15 days
• In case of delay, we charge statutory default interest
• Additional services outside the basic package are agreed in advance`
      },
      {
        title: '5. Liability',
        content: `• We are responsible for the correctness and timeliness of the services performed in accordance with applicable law
• We are not liable for errors resulting from incomplete or incorrect data you provide
• Our liability is limited to the amount of payments received in the last 12 months
• We have professional liability insurance`
      },
      {
        title: '6. Confidentiality',
        content: `• All information you entrust to us is treated as confidential
• We disclose data to third parties only with your consent or if required by law
• Our employees are bound to protect business secrets`
      },
      {
        title: '7. Duration and Termination',
        content: `• The contract is concluded for an indefinite period unless otherwise agreed
• Termination is possible with one month's notice
• Upon termination, we hand over all documentation in order and at no extra charge
• In case of serious violations, we may terminate the contract without notice`
      },
      {
        title: '8. Dispute Resolution',
        content: `• Slovenian law applies to the interpretation of these terms
• We will try to resolve any disputes amicably
• If this is not possible, the competent court is in Celje`
      },
      {
        title: '9. Changes to Terms',
        content: `• We may change these terms at any time
• We will notify you of changes by email
• Continued use of services after change means agreement with the new terms`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#F6F2EA]">
      <SEOHead
        title={`${content.title} — General Ledger`}
        description={content.metaDesc}
        canonicalPath={sl ? "/pogoji" : "/terms"}
        lang={sl ? 'sl' : 'en'}
        alternateUrl={sl ? '/terms' : '/pogoji'}
      />
      <NewTopBar />
      <NewHeader />
      
      <main className="max-w-[900px] mx-auto px-6 py-16 lg:py-24">
        <div className="mb-12">
          <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28] block mb-4">
            {sl ? 'PRAVNE INFORMACIJE' : 'LEGAL INFORMATION'}
          </span>
          <h1 className="font-serif text-4xl lg:text-[56px] leading-[1.05] tracking-[-1px] text-[#15181C] mb-4">
            {content.title}
          </h1>
          <p className="text-[15px] text-[#7A5B28]">{content.lastUpdated}</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-[17px] leading-[1.7] text-[#4E5359] mb-10">
            {content.intro}
          </p>

          <hr className="border-[#E2DACB] my-10" />

          {content.sections.map((section) => (
            <section key={section.title} className="mb-10">
              <h2 className="font-serif text-2xl lg:text-[32px] text-[#15181C] mb-4">
                {section.title}
              </h2>
              <LegalText content={section.content} />
            </section>
          ))}
          
          {/* Additional SEO content */}
          <div className="border-t border-[#E2DACB] pt-8 mt-4">
            <p className="text-[14px] text-[#7E786D] leading-[1.8]">
              {sl 
                ? 'GENERAL LEDGER d.o.o. je računovodski servis s sedežem na Celjski cesti 47, 3252 Rogatec, Slovenija. Od leta 2003 nudimo storitve ustanovitve podjetij, računovodstva, pridobivanja delovnih dovoljenj in davčnega svetovanja. Za vprašanja o naših storitvah nas kontaktirajte na info@general-ledger.eu ali +386 3 818 40 60. Delovni čas: ponedeljek do petek, 08:00–16:00.'
                : 'GENERAL LEDGER d.o.o. is an accounting firm located at Celjska cesta 47, 3252 Rogatec, Slovenia. Since 2003, we have been providing company formation, accounting, work permit, and tax advisory services. For questions about our services, contact us at info@general-ledger.eu or +386 3 818 40 60. Working hours: Monday to Friday, 08:00–16:00.'}
            </p>
          </div>
        </div>
      </main>

      <NewFooter />
    </div>
  );
};

export default TermsOfService;
